import * as commonActions from "../../../Common/actions";
import { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { checkValidation, toastError, toastSuccess } from "../../../../utils";
import { useDispatch } from "react-redux";
import { portsByChar, updateVessel } from "../actions";
import AddNewVessel from "../../../../components/Dashboard/AddNewVessel";
import { debounce } from "lodash";

export const initialRowData = {
    vesselName: "",
    vesselDwt: 0,
    vesselIMO: 0,
    vesselBalSpd1: 0,
    vesselBalConsmHS1: 0,
    vesselBalConsmLS1: 0,
    vesselBalConsmMGO1: 0,
    vesselBalSpd2: 0,
    vesselBalConsmHS2: 0,
    vesselBalConsmLS2: 0,
    vesselBalConsmMGO2: 0,
    vesselBalSpd3: 0,
    vesselBalConsmHS3: 0,
    vesselBalConsmLS3: 0,
    vesselBalConsmMGO3: 0,
    vesselBalSpd4: 0,
    vesselBalConsmHS4: 0,
    vesselBalConsmLS4: 0,
    vesselBalConsmMGO4: 0,
    vesselLdnSpd1: 0,
    vesselLdnConsmHS1: 0,
    vesselLdnConsmLS1: 0,
    vesselLdnConsmMGO1: 0,
    vesselLdnSpd2: 0,
    vesselLdnConsmHS2: 0,
    vesselLdnConsmLS2: 0,
    vesselLdnConsmMGO2: 0,
    vesselLdnSpd3: 0,
    vesselLdnConsmHS3: 0,
    vesselLdnConsmLS3: 0,
    vesselLdnConsmMGO3: 0,
    vesselLdnSpd4: 0,
    vesselLdnConsmHS4: 0,
    vesselLdnConsmLS4: 0,
    vesselLdnConsmMGO4: 0,
    vesselPIdleConsmHS: 0,
    vesselPIdleConsmLS: 0,
    vesselPIdleConsmMGO: 0,
    vesselPLdgConsmHS: 0,
    vesselPLdgConsmLS: 0,
    vesselPLdgConsmMGO: 0,
    vesselPDischConsmHS: 0,
    vesselPDischConsmLS: 0,
    vesselPDischConsmMGO: 0,
    vesselVExtraConsmHS: 0,
    vesselVExtraConsmLS: 0,
    vesselVExtraConsmMGO: 0,
    vesselBalComb1Selected: false,
    vesselBalComb2Selected: false,
    vesselBalComb3Selected: false,
    vesselBalComb4Selected: false,
    vesselLadComb1Selected: false,
    vesselLadComb2Selected: false,
    vesselLadComb3Selected: false,
    vesselLadComb4Selected: false,
    vesselExtraDetails: {},
};

export const initialVessel = {
    bnkDelhs: 0,
    bnkDells: 0,
    bnkDelmgo: 0,
    bnkPriceDeliveryhs: 0,
    bnkPriceDeliveryls: 0,
    bnkPriceDeliverymgo: 0,
    bnkPriceRedeliveryhs: 0,
    bnkPriceRedeliveryls: 0,
    bnkPriceRedeliverymgo: 0,
    bnkPriceSettlemenths: 0,
    bnkPriceSettlementls: 0,
    bnkPriceSettlementmgo: 0,
    bnkQtyRedeliveryhs: 0,
    bnkQtyRedeliveryls: 0,
    bnkQtyRedeliverymgo: 0,
    bnkRedeliveryQtyTolerancehs: 0,
    bnkRedeliveryQtyTolerancels: 0,
    bnkRedeliveryQtyTolerancemgo: 0,
    bunkerPriceOverridden: false,
    dateOpen: Date.now(),
};

const VesselViewModel = forwardRef(
    (
        {
            currentContract,
            handleClose,
            isFromCreateVessel = true,
            refetch,
            type = 'vesselContractIn',
            isOps = true,
            isViewOnly = false,
            showCommissionFields = false,
            extraDetails,
            setExtraDetails,
            setLoader = () => { },
            onSaveOverride = null
        },
        ref
    ) => {
        const [vessel, setVessel] = useState({});
        const [portOptions, setPortOptions] = useState([]);
        const [portLoading, setPortLoading] = useState(false);
        const dispatch = useDispatch();

        const delayPortSearch = useCallback(debounce((search) => {
            if (!search?.trim()) {
                setPortOptions([]);
                setPortLoading(false);
                return;
            }
            setPortLoading(true);
            portsByChar(search)
                .then((res) => {
                    setPortOptions(Array.isArray(res?.data?.list)
                        ? res.data.list.map((item) => ({
                            ...item,
                            portLabel: `${item.name}${item.country ? ` (${item.country})` : ""}`,
                        }))
                        : []);
                })
                .catch(() => setPortOptions([]))
                .finally(() => setPortLoading(false));
        }, 500), []);

        useEffect(() => () => delayPortSearch.cancel(), [delayPortSearch]);

        const onChangePort = (_name, selectedPort) => {
            setVessel((prev) => ({
                ...prev,
                port: selectedPort?.name || "",
                selectedPort,
                lat: selectedPort?.lat || "",
                lng: selectedPort?.lng || "",
            }));
        };

        useEffect(() => {
            let parsedVessel = {};
            try {
                if (currentContract?.vessel && typeof currentContract.vessel === "string") {
                    parsedVessel = JSON.parse(currentContract.vessel);
                } else if (currentContract?.vessel && typeof currentContract.vessel === "object") {
                    parsedVessel = currentContract.vessel;
                }
            } catch (e) { }

            setVessel({
                ...initialVessel,
                ...parsedVessel,
                selectedVessel: {
                    ...initialRowData,
                    ...parsedVessel,
                    ...(typeof currentContract?.vessel === "object" ? currentContract.vessel : parsedVessel),
                },
                ...currentContract,
            });
        }, [currentContract]);

        const handleVesselChange = (e, isExtra = false, isCheckbox = false) => {
            let name = "";
            let value = "";
            let type = "text";
            let checked = false;

            if (e?.target) {
                ({ name = "", value = "", type = "text", checked = false } = e.target);
            } else if (e?._isAMomentObject) {
                name = "dateOpen";
                value = e.toISOString();
            } else if (typeof e === "string" || typeof e === "number") {
                value = e;
            }

            const isCheck = isCheckbox || type === "checkbox";
            const val = isCheck ? checked : value;

            setVessel((prev) => {
                const updated = { ...prev };
                if (isExtra) {
                    const extra = { ...(updated.vesselExtraDetails || {}) };
                    extra[name] = val;
                    updated.vesselExtraDetails = extra;
                } else {
                    updated[name] = val;
                    if (updated.selectedVessel) {
                        updated.selectedVessel = {
                            ...updated.selectedVessel,
                            [name]: val,
                        };
                    }
                }
                return updated;
            });
        };

        const addNewSplitHire = () => {
            let splitHire =
                (vessel && vessel.splitHire && JSON.parse(vessel.splitHire)) || [];
            if (!!splitHire.find((item) => item.NoDays === 0)) return;
            splitHire.push({ NoDays: 0, applied: 0, disable: false, hireRate: 0 });
            setVessel({
                ...vessel,
                splitHire: JSON.stringify(splitHire),
            });
        };

        const onChangeBunkerPriceOverridden = (e) => {
            const name = e?.target?.name || "bunkerPriceOverridden";
            const checked = e?.target?.checked !== undefined
                ? Boolean(e.target.checked)
                : (typeof e === "boolean" ? e : !vessel.bunkerPriceOverridden);

            setVessel((prev) => {
                const updated = {
                    ...prev,
                    [name]: checked,
                    bunkerPriceOverridden: checked,
                };
                if (updated.selectedVessel) {
                    updated.selectedVessel = {
                        ...updated.selectedVessel,
                        [name]: checked,
                        bunkerPriceOverridden: checked,
                    };
                }
                return updated;
            });
        };

        const onChangeSplitHire = (index, e, isDelete = false) => {
            let splitHire =
                (vessel && vessel.splitHire && JSON.parse(vessel.splitHire)) || [];
            if (isDelete) splitHire.splice(index, 1);
            else {
                const { name = "", value = "" } = e.target;
                splitHire[index][name] = Number(value);
            }
            setVessel({
                ...vessel,
                splitHire: JSON.stringify(splitHire),
            });
        };

        const onOpenErrorAlert = (errors = []) => {
            dispatch(commonActions.openErrorAlert({ errorList: errors }));
        };

        const onSubmitUpdateVessel = () => {
            if (vessel?.selectedVessel) {
                let updatedVal = vessel;
                const currentVesselName = vessel?.selectedVessel?.vesselName || 'vessel';
                const error = checkValidation(vessel, { ...vessel?.selectedVessel, vesselName: currentVesselName }, 4);
                if (error?.length) {
                    return onOpenErrorAlert(error);
                }
                updatedVal.isValidate = true;
                const { selectedVessel, contractId, ...rest } = updatedVal;
                const mergedVesselObj = {
                    ...initialRowData,
                    ...selectedVessel,
                    ...rest,
                };
                updatedVal = {
                    ...rest,
                    type: type,
                    isOps,
                    vessel: JSON.stringify(mergedVesselObj),
                };

                if (onSaveOverride) {
                    onSaveOverride(updatedVal);
                    return;
                }

                setLoader(true);
                updateVessel({ _id: contractId }, updatedVal)
                    .then(() => {
                        setLoader(false);
                        toastSuccess("vessel updated successful");
                        handleClose();
                        if (refetch) refetch();
                    })
                    .catch((err) => {
                        setLoader(false);
                        toastError(err?.response?.message || err?.message);
                    });
            }
        };

        useImperativeHandle(ref, () => ({
            submit: onSubmitUpdateVessel,
        }));

        const combinedVessel = {
            ...initialRowData,
            ...initialVessel,
            ...(vessel?.selectedVessel || {}),
            ...vessel,
            vesselTextDescription: vessel?.vesselTextDescription || vessel?.selectedVessel?.vesselExtraDetails?.vesselTextDescription || vessel?.remark || "",
        };
        const contractFieldRows = [
            [{ name: "tcAdcom", label: "Adcom (%)" }, { name: "brokerage", label: "Brokerage (%)" }],
            [{ name: "minDuration", label: "Min Duration (days)" }, { name: "maxDuration", label: "Max Duration (days)" }],
            [{ name: "tcGbb", label: "TC GBB" }, { name: "voyageDuration", label: "Voyage Duration (days)" }],
            [{ name: "cvePerDay", label: "CVE Per Day", expenseField: true }, { name: "ilohc", label: "ILOHC", expenseField: true }],
            [{ name: "hireType", label: "Hire Type" }],
        ];

        return (
            <div className={`w-full ${isViewOnly ? "pointer-events-none opacity-90" : ""}`}>
                <AddNewVessel
                    vessel={combinedVessel}
                    showContractLabels={showCommissionFields}
                    contractFields={showCommissionFields ? (
                        <>
                                {contractFieldRows.map((row) => (
                                    <tr key={row[0].name}>
                                        {row.map(({ name, label, expenseField }) => (
                                            <Fragment key={name}>
                                                <th scope="row" style={{ width: "33%" }}>{label}</th>
                                                <td colSpan={name === "hireType" ? 3 : undefined} style={{ width: name === "hireType" ? "67%" : "17%" }}>
                                                    {name === "hireType" ? (
                                                        <select name={name} value={vessel?.hireType ?? "single"} onChange={handleVesselChange} disabled={isViewOnly} className="w-full h-full bg-[#ffffd6] text-[11px]">
                                                            <option value="single">Single Voyage</option>
                                                            <option value="multiple">Multiple Voyages</option>
                                                        </select>
                                                    ) : (
                                                        <input type="number" min="0" max={name === "tcAdcom" || name === "brokerage" ? 100 : undefined} step="0.01" name={name} value={expenseField ? (extraDetails?.[name] ?? "") : (vessel?.[name] ?? "")} onChange={expenseField ? (event) => setExtraDetails?.({ ...extraDetails, [name]: event.target.value }) : handleVesselChange} disabled={isViewOnly} style={{ backgroundColor: "#ffffd6" }} />
                                                    )}
                                                </td>
                                            </Fragment>
                                        ))}
                                    </tr>
                                ))}
                        </>
                    ) : null}
                    savePortList={portOptions}
                    portLoading={[portLoading]}
                    delayPortSearch={delayPortSearch}
                    onChangePort={onChangePort}
                    onChangeHandler={handleVesselChange}
                    onChangeSplitHire={onChangeSplitHire}
                    onChangeBunkerPriceOverridden={onChangeBunkerPriceOverridden}
                    addNewSplitHire={addNewSplitHire}
                />
            </div>
        );
    }
);

export default VesselViewModel;
